/* eslint formatjs/no-literal-string-in-jsx: "error" */
import React, { useEffect } from "react"
import { Spacer } from "components/views/atoms/Atoms"
import { Body } from "components/views/atoms/Bodies"
import ErrorMessageMarkdown from "components/views/login/ErrorMessageMarkdown"
import { useSignUpFindAccount } from "./context/SignUpFindAccountContext"
import { TextLink } from "../views/atoms/links/TextLink"
import queryString from "query-string"
import { useHistory, useLocation } from "react-router"
import { LeftBackArrowImage } from "../views/atoms/Molecules"
import { FlexContainer, H1, H2 } from "@carrotfertility/carotene"
import { SignUpFooter } from "components/signup"
import { AppAccessPageLayout } from "components/access-page/AppAccessPageLayout"
import { AppAccessPageCard } from "components/access-page/AppAccessPageCard"
import { UnauthPreferredLocale } from "components/views/UnauthPreferredLocale"
import { Box, Button, Stack, dayjs, useTheme } from "@carrotfertility/carotene-core"
import { FormattedMessage, useIntl } from "react-intl"
import { getErrorMessageFromCode } from "../../utils/CarrotErrors"
import { Form, FormDatePicker, FormTextField, useFormContext } from "@carrotfertility/carotene-core-x"
import { validateMemberDOB } from "services/common-forms"

function FindAccountBackArrow(): JSX.Element {
  const history = useHistory()
  return (
    <div
      className="pointer bg-carrot-orange-01 br-100 flex items-center justify-center"
      style={{
        height: "2rem",
        width: "2rem"
      }}
      data-testid="back-arrow"
      onClick={() => history.push("/signup")}
    >
      <LeftBackArrowImage />
    </div>
  )
}

function SignUpFindAccountHeader({ fromSignup }: { fromSignup: boolean }): JSX.Element {
  const { errorMessage, showTryAgain } = useSignUpFindAccount()
  const intl = useIntl()
  const headerCopy = fromSignup
    ? intl.formatMessage({ defaultMessage: "Next, share your name and date of birth" })
    : intl.formatMessage({ defaultMessage: "Sign up for Carrot" })

  return (
    <>
      {fromSignup ? (
        <>
          <FindAccountBackArrow />
          <Spacer height={1.5} />
        </>
      ) : (
        <Spacer height={2.5} />
      )}
      {showTryAgain ? (
        <H1>
          <FormattedMessage defaultMessage="Try again" />
        </H1>
      ) : (
        <H1>{headerCopy}</H1>
      )}
      {errorMessage ? (
        <>
          <Spacer height={0.5} />
          <ErrorMessageMarkdown align={"tl"} source={intl.formatMessage(getErrorMessageFromCode(errorMessage))} />
        </>
      ) : (
        <>
          {showTryAgain && (
            <>
              <Body color="black-80">
                <FormattedMessage defaultMessage="Sorry, we couldn't find your account with the information provided." />
              </Body>
              <Spacer height={1.5} />
              <Body color="black-80">
                <FormattedMessage
                  defaultMessage="Try again or <link>contact our Care Team</link> for help."
                  values={{ link: (linkContent) => <TextLink to="/signup-get-help">{linkContent}</TextLink> }}
                />
              </Body>
            </>
          )}
        </>
      )}
    </>
  )
}

function SignUpFindAccountFooter({ fromSignup }: { fromSignup: boolean }): JSX.Element {
  return (
    <>
      <Spacer height={3} />
      {fromSignup ? null : <SignUpFooter />}
    </>
  )
}

function HiddenEmail() {
  const { register } = useFormContext()
  return <input hidden {...register("email")} />
}

function SignUpFindAccountForm({
  fromSignup,
  email
}: {
  fromSignup?: boolean
  email?: string | string[]
}): JSX.Element {
  const { shake, inputsEnabled, onSubmit } = useSignUpFindAccount()
  const theme = useTheme()
  const intl = useIntl()

  return (
    <AppAccessPageCard shake={shake}>
      <Form reValidateMode="onSubmit" defaultValues={{ email }} onSubmit={onSubmit}>
        <Spacer height={1.5} />
        <SignUpFindAccountHeader {...{ fromSignup }} />
        <Spacer height={3} />
        <H2 variant="primary">
          <FormattedMessage defaultMessage="What's your legal name?" />
        </H2>
        <Spacer height={1} />
        <HiddenEmail />
        <FlexContainer>
          <div className="flex-auto" style={{ flexBasis: 0 }}>
            <FormTextField
              name="firstName"
              registerOptions={{ required: intl.formatMessage({ defaultMessage: "First Name is required" }) }}
              disabled={!inputsEnabled}
              label={intl.formatMessage({ defaultMessage: "First Name" })}
            />
          </div>
          <Spacer width={1} />
          <div className="flex-auto" style={{ flexBasis: 0 }}>
            <FormTextField
              name="lastName"
              registerOptions={{ required: intl.formatMessage({ defaultMessage: "Last name is required" }) }}
              disabled={!inputsEnabled}
              label={intl.formatMessage({ defaultMessage: "Last Name" })}
            />
          </div>
        </FlexContainer>
        <Spacer height={3} />
        <H2 variant="primary">
          <FormattedMessage defaultMessage="What's your date of birth?" />
        </H2>
        <Spacer height={1} />
        <FormDatePicker
          name="dateOfBirth"
          controllerProps={{
            rules: {
              required: intl.formatMessage({ defaultMessage: "Required" }),
              validate: (value: dayjs.Dayjs) => {
                const result = validateMemberDOB(value)
                if (typeof result !== "boolean") {
                  return intl.formatMessage(result)
                }
                return result
              }
            }
          }}
          label={intl.formatMessage({ defaultMessage: "Date of birth" })}
        />
        <Spacer height={3} />
        <Stack spacing={theme.spacing(theme.tokens.spacing.lg)}>
          <FlexContainer justifyContent="center">
            <Button type="submit" disabled={!inputsEnabled}>
              <FormattedMessage defaultMessage="Continue" />
            </Button>
          </FlexContainer>
          <Box display="block" sx={{ alignSelf: "center" }}>
            <UnauthPreferredLocale />
          </Box>
        </Stack>
        <SignUpFindAccountFooter {...{ fromSignup }} />
      </Form>
    </AppAccessPageCard>
  )
}

export function SignUpFindAccountPage(): JSX.Element {
  const location = useLocation()
  const history = useHistory()
  const { email } = queryString.parse(location.search)
  const intl = useIntl()

  useEffect(() => {
    if (!email) {
      history.push("/signup")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- See https://carrotfertility.atlassian.net/wiki/spaces/PE/pages/2050295461/Remove+Build+Warnings#react-hooks%2Fexhaustive-deps
  }, [])

  return (
    <AppAccessPageLayout title={intl.formatMessage({ defaultMessage: "Sign up" })}>
      <SignUpFindAccountForm {...{ email }} fromSignup />
    </AppAccessPageLayout>
  )
}

export function SignUpInfoPage(): JSX.Element {
  const intl = useIntl()
  return (
    <AppAccessPageLayout title={intl.formatMessage({ defaultMessage: "Sign up" })}>
      <SignUpFindAccountForm />
    </AppAccessPageLayout>
  )
}
